import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import LoadingSpinner from './UI/LoadingSpinner'

import { API_BASE_URL } from '../config/api'

const EVENTS_PER_PAGE = 6

const EventDiscoveryWidget = () => {
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedGenre, setSelectedGenre] = useState('All Music')
  const [zipCode, setZipCode] = useState('')
  const [debouncedZipCode, setDebouncedZipCode] = useState('')
  const [sortBy, setSortBy] = useState('date')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [error, setError] = useState(null)
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Music genre filters - using popular search terms for each genre
  const musicGenres = [
    { name: 'All Music', query: 'concert', classification: 'music' },
    { name: 'Rock', query: 'rock', classification: 'music' },
    { name: 'Pop', query: 'pop', classification: 'music' },
    { name: 'Hip-Hop', query: 'hip hop', classification: 'music' },
    { name: 'EDM', query: 'edm', classification: 'music' },
    { name: 'Country', query: 'country', classification: 'music' },
    { name: 'Jazz', query: 'jazz', classification: 'music' },
    { name: 'Classical', query: 'classical', classification: 'music' }
  ]

  // Debounce zip code input - only update after user stops typing
  useEffect(() => {
    // Only debounce if zip code is being typed (not empty or complete)
    if (zipCode === '' || zipCode.length === 5) {
      setDebouncedZipCode(zipCode)
    } else {
      const timer = setTimeout(() => {
        setDebouncedZipCode(zipCode)
      }, 800)

      return () => clearTimeout(timer)
    }
  }, [zipCode])

  useEffect(() => {
    fetchPopularEvents()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre, debouncedZipCode, sortBy, currentPage])

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0)
  }, [selectedGenre, debouncedZipCode, sortBy])

  const fetchPopularEvents = async () => {
    try {
      setLoading(true)
      setError(null)

      const selectedGenreObj = musicGenres.find(g => g.name === selectedGenre) || musicGenres[0]

      const params = {
        q: selectedGenreObj.query,
        classification: selectedGenreObj.classification,
        size: EVENTS_PER_PAGE,
        page: currentPage,
        sort: sortBy
      }

      // Add zip code to params if provided and valid (5 digits)
      // Ticketmaster API uses 'postalCode' parameter for filtering by zip code
      if (debouncedZipCode.trim() && debouncedZipCode.length === 5) {
        params.postalCode = debouncedZipCode.trim()
        // Also add radius to get events within certain distance (in miles)
        params.radius = '50'
        params.unit = 'miles'
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/events/ticketmaster/search`,
        {
          params,
          withCredentials: true
        }
      )

      setEvents(response.data.events || [])
      // Handle pagination data from response (returned at top level, not nested)
      setTotalPages(response.data.totalPages || 1)
      setTotalElements(response.data.totalElements || 0)
    } catch (err) {
      setError('Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  const handleEventClick = (event) => {
    // Navigate to event preview page first
    navigate('/discover/event', { state: { event } })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'TBA'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage)
    }
  }

  return (
    <div className="w-full">
      {/* Header with Collapse Toggle */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <h2
            className="text-2xl font-bold"
            style={{color: 'var(--theme-text-primary)'}}
          >
            Discover Music Events
          </h2>
          <span
            className={`transform transition-transform duration-300 ${isCollapsed ? '-rotate-90' : 'rotate-0'}`}
            style={{color: 'var(--theme-text-secondary)'}}
          >
            ▼
          </span>
        </button>

        {/* Event count badge */}
        {!isCollapsed && totalElements > 0 && (
          <span
            className="text-sm px-3 py-1 rounded-full bg-white/10"
            style={{color: 'var(--theme-text-secondary)'}}
          >
            {totalElements.toLocaleString()} events found
          </span>
        )}
      </div>

      {/* Collapsible Content */}
      <div className={`transition-all duration-300 overflow-hidden ${isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
        {/* Filters Section */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            {/* Zip Code Filter and Sort */}
            <div className="flex items-center gap-2">
              {/* Sort Dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 focus:border-white/40 focus:bg-white/15 transition-all duration-300 outline-none cursor-pointer"
                style={{color: 'var(--theme-text-primary)'}}
              >
                <option value="date" className="bg-gray-800">Date (Soonest)</option>
                <option value="relevance" className="bg-gray-800">Relevance</option>
                <option value="name" className="bg-gray-800">Name (A-Z)</option>
              </select>

              {/* Zip Code Filter */}
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter Zip Code"
                value={zipCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '') // Only allow digits
                  setZipCode(value)
                }}
                maxLength={5}
                className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 focus:border-white/40 focus:bg-white/15 transition-all duration-300 outline-none w-40"
                style={{color: 'var(--theme-text-primary)'}}
              />
              {zipCode && (
                <button
                  onClick={() => setZipCode('')}
                  className="px-3 py-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                  style={{color: 'var(--theme-text-secondary)'}}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Genre Filter Tabs */}
          <div className="flex flex-wrap gap-2">
            {musicGenres.map((genre) => (
              <button
                key={genre.name}
                onClick={() => setSelectedGenre(genre.name)}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                  selectedGenre === genre.name
                    ? 'bg-white/30 backdrop-blur-md border-2 shadow-lg scale-105'
                    : 'bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 hover:scale-105'
                }`}
                style={{
                  color: selectedGenre === genre.name
                    ? 'var(--theme-text-primary)'
                    : 'var(--theme-text-secondary)',
                  borderColor: selectedGenre === genre.name
                    ? 'var(--theme-accent)'
                    : 'transparent'
                }}
              >
                {genre.name}
              </button>
            ))}
          </div>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div
            className="text-center py-8 px-4 bg-red-500/10 backdrop-blur-md rounded-2xl border border-red-500/30"
            style={{color: 'var(--theme-text-secondary)'}}
          >
            {error}
          </div>
        ) : events.length === 0 ? (
          <div
            className="text-center py-8 px-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20"
            style={{color: 'var(--theme-text-secondary)'}}
          >
            No upcoming events found
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((event, index) => (
                <div
                  key={index}
                  onClick={() => handleEventClick(event)}
                  className="group relative bg-white/10 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 overflow-hidden hover:scale-105 hover:shadow-2xl hover:bg-white/15 transition-all duration-300 cursor-pointer"
                >
                  {/* Event Image */}
                  {event.images && event.images.length > 0 ? (
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={event.images[0].url || event.images[0]}
                        alt={event.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.parentElement.innerHTML = `
                            <div class="h-40 flex items-center justify-center" style="background: linear-gradient(135deg, var(--theme-gradient-start), var(--theme-gradient-end))">
                              <span class="text-4xl opacity-50">🎵</span>
                            </div>
                          `
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                  ) : (
                    <div
                      className="h-40 flex items-center justify-center"
                      style={{
                        background: 'linear-gradient(135deg, var(--theme-gradient-start), var(--theme-gradient-end))'
                      }}
                    >
                      <span className="text-4xl opacity-50">🎵</span>
                    </div>
                  )}

                  {/* Event Content */}
                  <div className="p-4">
                    {/* Event Name */}
                    <h3
                      className="text-sm font-bold mb-2 line-clamp-2 min-h-[2.5rem]"
                      style={{color: 'var(--theme-text-primary)'}}
                    >
                      {event.name}
                    </h3>

                    {/* Venue */}
                    {event.venue && (
                      <p
                        className="text-xs mb-1 flex items-center gap-1 line-clamp-1"
                        style={{color: 'var(--theme-text-secondary)'}}
                      >
                        <span className="opacity-70">📍</span>
                        {event.venue.name}
                      </p>
                    )}

                    {/* Location */}
                    {event.venue && (
                      <p
                        className="text-xs mb-2 line-clamp-1"
                        style={{color: 'var(--theme-text-muted)'}}
                      >
                        {[event.venue.city, event.venue.state, event.venue.country]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}

                    {/* Date */}
                    <div
                      className="text-xs font-semibold flex items-center gap-1"
                      style={{color: 'var(--theme-accent)'}}
                    >
                      <span>📅</span>
                      {formatDate(event.date)}
                    </div>

                    {/* Price Range (if available) */}
                    {event.priceRange && (
                      <div
                        className="text-xs mt-2 pt-2 border-t border-white/10"
                        style={{color: 'var(--theme-text-muted)'}}
                      >
                        ${event.priceRange.min} - ${event.priceRange.max}
                      </div>
                    )}
                  </div>

                  {/* Hover Indicator */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                    style={{background: 'var(--theme-accent)'}}
                  ></div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                    currentPage === 0
                      ? 'bg-white/5 cursor-not-allowed opacity-50'
                      : 'bg-white/10 hover:bg-white/20 hover:scale-105'
                  }`}
                  style={{color: 'var(--theme-text-secondary)'}}
                >
                  ← Prev
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {/* Show first page */}
                  {currentPage > 2 && (
                    <>
                      <button
                        onClick={() => handlePageChange(0)}
                        className="w-10 h-10 rounded-xl font-medium transition-all duration-300 bg-white/10 hover:bg-white/20"
                        style={{color: 'var(--theme-text-secondary)'}}
                      >
                        1
                      </button>
                      {currentPage > 3 && (
                        <span style={{color: 'var(--theme-text-muted)'}}>...</span>
                      )}
                    </>
                  )}

                  {/* Show surrounding pages */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i
                    } else if (currentPage < 3) {
                      pageNum = i
                    } else if (currentPage > totalPages - 4) {
                      pageNum = totalPages - 5 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }

                    if (pageNum < 0 || pageNum >= totalPages) return null
                    if (currentPage > 2 && pageNum === 0) return null
                    if (currentPage < totalPages - 3 && pageNum === totalPages - 1) return null

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-xl font-medium transition-all duration-300 ${
                          currentPage === pageNum
                            ? 'bg-white/30 scale-110 shadow-lg'
                            : 'bg-white/10 hover:bg-white/20'
                        }`}
                        style={{
                          color: currentPage === pageNum
                            ? 'var(--theme-text-primary)'
                            : 'var(--theme-text-secondary)',
                          borderColor: currentPage === pageNum ? 'var(--theme-accent)' : 'transparent',
                          borderWidth: currentPage === pageNum ? '2px' : '0'
                        }}
                      >
                        {pageNum + 1}
                      </button>
                    )
                  })}

                  {/* Show last page */}
                  {currentPage < totalPages - 3 && totalPages > 5 && (
                    <>
                      {currentPage < totalPages - 4 && (
                        <span style={{color: 'var(--theme-text-muted)'}}>...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(totalPages - 1)}
                        className="w-10 h-10 rounded-xl font-medium transition-all duration-300 bg-white/10 hover:bg-white/20"
                        style={{color: 'var(--theme-text-secondary)'}}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                    currentPage >= totalPages - 1
                      ? 'bg-white/5 cursor-not-allowed opacity-50'
                      : 'bg-white/10 hover:bg-white/20 hover:scale-105'
                  }`}
                  style={{color: 'var(--theme-text-secondary)'}}
                >
                  Next →
                </button>
              </div>
            )}

            {/* Page Info */}
            {totalPages > 1 && (
              <p
                className="text-center text-sm mt-3"
                style={{color: 'var(--theme-text-muted)'}}
              >
                Page {currentPage + 1} of {totalPages}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default EventDiscoveryWidget
